/**
 * Runtime configuration service.
 *
 * Mirrors `gen5-pwa-fresh/src/services/configService.ts`: fetches
 * `/config.json` once, caches the result, falls back to DEFAULT_CONFIG
 * on any error so dev mode keeps working without a config file.
 */

import { type AppConfig, DEFAULT_CONFIG } from "../types/config";

class ConfigService {
  private config: AppConfig | null = null;
  private loadPromise: Promise<AppConfig> | null = null;

  async loadConfig(): Promise<AppConfig> {
    if (this.config) return this.config;
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = this.fetchConfig();
    this.config = await this.loadPromise;
    return this.config;
  }

  /** Synchronous accessor for code paths that have already awaited loadConfig. */
  getConfig(): AppConfig {
    return this.config ?? DEFAULT_CONFIG;
  }

  private async fetchConfig(): Promise<AppConfig> {
    const publicUrl = process.env.PUBLIC_URL ?? "";
    try {
      const response = await fetch(`${publicUrl}/config.json?t=${Date.now()}`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) {
        // Quietly fall back; the shipped default points at /sample-boxes.json
        // so dev mode still renders something useful.
        // eslint-disable-next-line no-console
        console.warn(
          `configService: /config.json returned ${response.status}; using defaults`,
        );
        return rewriteLocalhostForRemoteOrigin(DEFAULT_CONFIG);
      }
      const cfg = (await response.json()) as AppConfig;
      const rewritten = rewriteLocalhostForRemoteOrigin(cfg);
      if (cfg.features?.enableDebugLogging) {
        // eslint-disable-next-line no-console
        console.log("[configService] loaded:", rewritten);
      }
      return rewritten;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("configService: load failed; using defaults", e);
      return rewriteLocalhostForRemoteOrigin(DEFAULT_CONFIG);
    }
  }
}

/**
 * When the configured API base URL points at `localhost`/`127.0.0.1` but the
 * page itself was loaded from a different host (e.g. someone hit the dev
 * server via a Tailscale IP), the friendly thing to do is rewrite the API
 * host to match — otherwise the API calls go to the FRIEND's localhost,
 * which has nothing running. Production configs use `""` (same-origin via
 * nginx) and aren't affected by this rewrite.
 */
function rewriteLocalhostForRemoteOrigin(cfg: AppConfig): AppConfig {
  if (typeof window === "undefined") return cfg;
  const browserHost = window.location.hostname;
  if (!browserHost || browserHost === "localhost" || browserHost === "127.0.0.1") {
    return cfg; // Browser IS on localhost — keep config as-is.
  }
  const fix = (url: string | undefined): string | undefined => {
    if (!url) return url;
    try {
      const u = new URL(url);
      if (u.hostname === "localhost" || u.hostname === "127.0.0.1") {
        u.hostname = browserHost;
        // eslint-disable-next-line no-console
        console.log(
          `[configService] rewrote ${url} -> ${u.toString()} (browser at ${browserHost})`,
        );
        return u.toString().replace(/\/+$/, "");
      }
    } catch {
      // Not a parseable absolute URL (e.g., "" for same-origin) — leave it.
    }
    return url;
  };
  return {
    ...cfg,
    mttr: {
      ...cfg.mttr,
      appApiBaseUrl: fix(cfg.mttr.appApiBaseUrl),
    },
  };
}

export const configService = new ConfigService();
